// API リクエストに付ける認証トークン(Cognito の ID トークン)の取得。plugins/axios.js の onRequest から使う。
//
// Amplify の Auth.currentSession() は、トークンの期限が切れていると裏でリフレッシュ通信を行う。
// その通信が一瞬失敗しただけでも reject されるため、以前はヘッダー無しで BE に送ってしまい、
// BE の 401 → 共通処理でサインアウト、という流れで利用者がログアウトされていた。
// ここでは 1 回だけ再試行し、それでも取れないときは「セッションが無い」のか「一時的に取れない」のかを区別する。
// 一時的に取れないときもリクエストはヘッダー無しで送る(認証不要の API は従来どおり成功する)が、
// request.tokenUnavailable を立てておき、onResponseError はその 401 ではサインアウトしない。

export const TOKEN_RETRY_DELAY_MS = 500
// 直前に「一時的に取れない」と判定してからこの時間内は再試行せず 1 回だけ試す
// (ページ遷移ごとに走るメンテナンス確認などが、失敗のたびに 500ms 待たされないようにする)
export const TOKEN_RETRY_COOLDOWN_MS = 5000
// 同じ種類の Sentry 通知を続けて送らない間隔(Cognito が不安定なときに利用者全員から大量に届かないように)
export const TOKEN_REPORT_COOLDOWN_MS = 60000

// 再試行しても解消しない失敗(ログインしていない / セッション情報が欠けている / リフレッシュトークンが無効 など)か。
// 通信失敗(amazon-cognito-identity-js の code 'NetworkError' / message 'Network error')は含めない。
//
// 判定に使うメッセージの出どころ
//   'No current user'                                          @aws-amplify/auth Auth.currentUserPoolUser (未ログイン。文字列で reject される)
//   'Local storage is missing an ID Token, Please authenticate' amazon-cognito-identity-js CognitoUser.getSession (ID トークンが無い)
//   'Cannot retrieve a new session. Please authenticate.'      同上 (期限切れでリフレッシュトークンが無い)
//   'User is not authenticated'                                同 CognitoUser (サインイン前の操作)
//   'Username is null. Cannot retrieve a new session'          同 CognitoUser.refreshSession
//   'Refresh Token has expired' / 'Refresh Token has been revoked' / 'Invalid Refresh Token'
//                                                              Cognito の NotAuthorizedException (リフレッシュ失敗)
export const isSessionLostError = (error) => {
  if (!error) {
    return false
  }
  const code = error.code || error.name || ''
  if (
    code === 'NotAuthorizedException' ||
    code === 'UserNotFoundException' ||
    code === 'PasswordResetRequiredException'
  ) {
    return true
  }
  const message = typeof error === 'string' ? error : error.message || ''
  return (
    /no current user/i.test(message) ||
    /please authenticate/i.test(message) ||
    /user is not authenticated/i.test(message) ||
    /cannot retrieve a new session/i.test(message) ||
    /refresh token/i.test(message)
  )
}

const defaultWait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// トークンを取得する。結果は次のいずれか
//   { status: 'ok', token, retried, error }     取得できた。retried は再試行の末に取れたか(その場合 error は 1 回目の失敗)
//   { status: 'session-lost', error }           セッションが無い(再試行しても解消しない)。ヘッダー無しで送る
//   { status: 'unavailable', error, retried }   一時的に取得できない(通信失敗など)。retried は再試行したうえで失敗したか
// retry=false のときは再試行せず 1 回だけ試す(retried=false になる)
export const resolveAuthToken = async ({
  getToken,
  retry = true,
  retryDelayMs = TOKEN_RETRY_DELAY_MS,
  wait = defaultWait
}) => {
  const attempts = retry ? 2 : 1
  let lastError
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const token = await getToken()
      if (token) {
        return attempt === 0
          ? { status: 'ok', token, retried: false }
          : { status: 'ok', token, retried: true, error: lastError }
      }
      // セッションはあるのにトークンが空。再試行で変わるものではないので、ヘッダー無しで送って BE の判断に任せる
      return { status: 'session-lost', error: new Error('empty token') }
    } catch (error) {
      lastError = error
      // Amplify は userPool 未設定のとき値なし(undefined)で reject する。再試行しても解消しないので session-lost 扱い
      if (error === undefined || error === null || isSessionLostError(error)) {
        return { status: 'session-lost', error }
      }
    }
    if (attempt < attempts - 1) {
      await wait(retryDelayMs)
    }
  }
  return { status: 'unavailable', error: lastError, retried: retry }
}

// resolveAuthToken を「同時に呼ばれたら 1 回の取得を共有し(単一飛行)、直前に unavailable だった直後は再試行しない」
// 形に包んだ関数を返す。ページ表示時に複数の API が同時に走ってもリフレッシュ通信は 1 系統で済む
export const createAuthTokenResolver = ({
  getToken,
  retryDelayMs = TOKEN_RETRY_DELAY_MS,
  cooldownMs = TOKEN_RETRY_COOLDOWN_MS,
  wait = defaultWait,
  now = () => Date.now()
}) => {
  let inFlight = null
  let lastUnavailableAt = -Infinity
  return () => {
    if (inFlight) {
      return inFlight
    }
    const retry = now() - lastUnavailableAt >= cooldownMs
    inFlight = resolveAuthToken({ getToken, retry, retryDelayMs, wait })
      .then((result) => {
        if (result.status === 'unavailable') {
          lastUnavailableAt = now()
        }
        return result
      })
      .finally(() => {
        inFlight = null
      })
    return inFlight
  }
}

// JWT(ID トークン)の exp(秒) を取り出す。壊れていれば null
export const getJwtExpiry = (token) => {
  try {
    const payload = String(token).split('.')[1]
    if (!payload) return null
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      Array.from(atob(base64))
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const exp = JSON.parse(json).exp
    return typeof exp === 'number' ? exp : null
  } catch (error) {
    return null
  }
}

// store に残っている ID トークンが、まだ API に使える(期限まで余裕がある)か
export const TOKEN_FALLBACK_MARGIN_SEC = 60
export const isTokenUsable = (token, now = () => Date.now()) => {
  if (!token || typeof token !== 'string') return false
  const exp = getJwtExpiry(token)
  return exp !== null && exp - TOKEN_FALLBACK_MARGIN_SEC > now() / 1000
}

// リクエストに付ける X-Authorization を決める。
//
// @nuxtjs/axios はこのプラグインの onRequest より後に組み込みの interceptor
// `config.headers = { ...defaults.headers.common, ...config.headers }` を走らせ、既定ヘッダー
// (ログイン処理が setHeader('X-Authorization', token) で入れたログイン時のトークン)を headers の
// トップレベルに展開する。axios は送信前に common → メソッド別 → トップレベル の順で上書きして
// 平坦化するため、ここで headers.common にだけ書いても既定ヘッダー(ログイン時のトークン)に負けて、
// ログイン後にページを再読み込みしない限り API には常にログイン時のトークンが送られていた
// (メールアドレス変更後の /v1/user/emailupdate が変更前の email クレームで届いていた原因)。
// トップレベルに書けば既定ヘッダーより優先される。common にも書いておき、hasAuthHeader などの判定を変えない
export const setAuthHeader = (request, token) => {
  request.headers = request.headers || {}
  request.headers['X-Authorization'] = token
  if (request.headers.common && typeof request.headers.common === 'object') {
    request.headers.common['X-Authorization'] = token
  }
  return request
}

export const TOKEN_UNAVAILABLE_MESSAGE =
  '通信に失敗しました。電波の良い場所で再度お試しください。'

// トークンが一時的に取れないまま送ったリクエストの 401 に印を付ける(トーストの文言と、サインアウトしない判定に使う)
export const markTokenUnavailable = (error) => {
  if (error && typeof error === 'object') {
    error.isTokenUnavailable = true
  }
  return error
}

// Sentry へ送る。診断用の処理なので、captureMessage 自体が同期例外を投げても API 送信には影響させない
const safeCaptureMessage = (sentry, message, captureContext) => {
  if (!sentry || typeof sentry.captureMessage !== 'function') {
    return false
  }
  try {
    sentry.captureMessage(message, captureContext)
    return true
  } catch (error) {
    return false
  }
}

// Sentry への通知。$sentry は @nuxtjs/sentry が注入するもので、無効化環境ではモックが入る。無い場合は何もしない
const captureTokenEvent = ({
  sentry,
  message,
  level,
  result,
  error,
  path,
  method,
  url
}) => {
  const cause = error == null ? {} : error
  return safeCaptureMessage(sentry, message, {
    level,
    tags: {
      feature: 'auth-token',
      result,
      cause_code: cause.code || cause.name || 'unknown'
    },
    extra: {
      cause_message:
        typeof error === 'string' ? error : cause.message || String(error),
      path,
      method,
      url
    }
  })
}

// 再試行しても取れなかった(ヘッダー無しで送った)ことを warning で送る。クールダウン内の 2 回目以降(retried=false)は送らない
export const reportTokenUnavailable = ({ sentry, error, path, method, url }) =>
  captureTokenEvent({
    sentry,
    message: '認証トークンを再試行しても取得できず、ヘッダー無しで送信',
    level: 'warning',
    result: 'unavailable',
    error,
    path,
    method,
    url
  })

// 1 回目は失敗したが再試行で取れた(利用者には影響なし)ことを info で送る。発生頻度を把握するため
export const reportTokenRecovered = ({ sentry, error, path, method, url }) =>
  captureTokenEvent({
    sentry,
    message: '認証トークンの取得が 1 回失敗し、再試行で成功',
    level: 'info',
    result: 'recovered',
    error,
    path,
    method,
    url
  })

// localStorage に残っている Cognito のセッション情報の有無(診断用)。
// Amplify(amazon-cognito-identity-js)は "CognitoIdentityServiceProvider.<clientId>.LastAuthUser" と
// "CognitoIdentityServiceProvider.<clientId>.<user>.idToken / accessToken / refreshToken" を保存している
export const inspectCognitoStorage = (storage) => {
  try {
    const target =
      storage || (typeof window !== 'undefined' ? window.localStorage : null)
    if (!target) {
      return { storage_accessible: false }
    }
    let keyCount = 0
    let hasLastAuthUser = false
    let hasIdToken = false
    let hasRefreshToken = false
    for (let i = 0; i < target.length; i++) {
      const key = target.key(i) || ''
      if (!key.startsWith('CognitoIdentityServiceProvider.')) {
        continue
      }
      keyCount++
      if (key.endsWith('.LastAuthUser')) hasLastAuthUser = true
      if (key.endsWith('.idToken')) hasIdToken = true
      if (key.endsWith('.refreshToken')) hasRefreshToken = true
    }
    return {
      storage_accessible: true,
      cognito_key_count: keyCount,
      has_last_auth_user: hasLastAuthUser,
      has_id_token: hasIdToken,
      has_refresh_token: hasRefreshToken
    }
  } catch (error) {
    return {
      storage_accessible: false,
      storage_error: (error && error.message) || String(error)
    }
  }
}

// ブラウザのサイトデータの状態(診断用)。退避(ストレージ逼迫時の自動削除)の裏付けに使う。
// persisted: 永続化が認められているか / usage, quota: 使用量と上限(MB)。取れない環境では項目を省く
export const inspectStorageQuota = async (nav) => {
  const target =
    nav || (typeof navigator !== 'undefined' ? navigator : undefined)
  const out = {}
  if (!target || !target.storage) return out
  try {
    if (typeof target.storage.persisted === 'function') {
      out.storage_persisted = await target.storage.persisted()
    }
  } catch (error) {}
  try {
    if (typeof target.storage.estimate === 'function') {
      const est = await target.storage.estimate()
      if (est && typeof est.usage === 'number')
        out.storage_usage_mb = Math.round(est.usage / 1048576)
      if (est && typeof est.quota === 'number')
        out.storage_quota_mb = Math.round(est.quota / 1048576)
    }
  } catch (error) {}
  return out
}

// 直前までトークンがあったのに「セッションが無い」と判定された(ヘッダー無しで送り、401 でログイン画面へ遷移する)
// ことを warning で送る。原因の切り分け用に Amplify のエラー内容と localStorage の状態を付ける
export const reportSessionLost = async ({
  sentry,
  error,
  path,
  method,
  url,
  storage,
  nav,
  fallback = false
}) => {
  if (!sentry || typeof sentry.captureMessage !== 'function') {
    return false
  }
  const cause = error == null ? {} : error
  // localStorage の状態は await の前(同期)に取る。storage.estimate() を待つ間に後続の requiredAuth が signOut して
  // キーを消すと、「ブラウザが消した(0 件)」と「アプリが消した」の区別がつかなくなるため
  const cognitoStorage = inspectCognitoStorage(storage)
  const quota = await inspectStorageQuota(nav)
  return safeCaptureMessage(
    sentry,
    fallback
      ? '直前までトークンがあったのにセッション無しと判定(メモリ上のトークンで送信)'
      : '直前までトークンがあったのにセッション無しと判定(ヘッダー無しで送信、401 でログイン画面へ)',
    {
      level: 'warning',
      tags: {
        feature: 'auth-token',
        result: 'session-lost',
        fallback: fallback ? 'memory-token' : 'none',
        cause_code: cause.code || cause.name || 'unknown'
      },
      extra: {
        cause_message:
          error === undefined || error === null
            ? '(値なしで reject)'
            : typeof error === 'string'
            ? error
            : cause.message || String(error),
        path,
        method,
        url,
        ...cognitoStorage,
        ...quota
      }
    }
  )
}

// 画面のパスからクエリとハッシュを落とす(Sentry に送る用。/app/*?userId=<ID トークン> のようにクエリに秘匿情報が入るため)
export const stripQuery = (path) => {
  if (typeof path !== 'string') return path
  return path.split('#')[0].split('?')[0]
}

// Auth.signOut() を呼んだことを理由付きで info 送信する(切り分け用)。
//   reason: 'response-401'(API の 401 共通処理) / 'middleware-no-token'(requiredAuth: store にトークン無し) /
//           'user-logout'(ログアウト操作) / 'login-register-failed'(ログイン直後の /v1/user/register 失敗)
// signOut は同一サイトの localStorage を全タブ共通で消すため、別タブで操作中の利用者に session-lost が起きる経路になり得る。
// session-lost 通知と同時刻・同一 IP にこの通知があれば、その経路だったと判断できる
export const reportSignOut = ({
  sentry,
  reason,
  path,
  method,
  url,
  hadToken,
  tokenStatus,
  storage
}) =>
  safeCaptureMessage(sentry, `Auth.signOut を実行(${reason})`, {
    level: 'info',
    tags: {
      feature: 'auth-token',
      result: 'sign-out',
      reason: reason || 'unknown'
    },
    extra: {
      path,
      method,
      url,
      had_token: !!hadToken,
      token_status: tokenStatus || '-',
      ...inspectCognitoStorage(storage)
    }
  })

// ヘッダー無しで送った API が 401 を返した(= 認証必須の API をヘッダー無しで呼んだ)ことを warning で送る。
// session-lost 通知は「直前まで store にトークンがあった」場合だけなので、未ログイン相当の状態で認証必須の API が
// 呼ばれたケース(FE に記録が残らず BE の 401 だけが見える)を補う。store の状態は had_token で区別する
export const reportUnauthorizedWithoutHeader = ({
  sentry,
  tokenStatus,
  hadToken,
  path,
  method,
  url,
  storage
}) =>
  safeCaptureMessage(
    sentry,
    '認証必須の API をヘッダー無しで呼び 401(store のトークン有無に関係なく記録)',
    {
      level: 'warning',
      tags: {
        feature: 'auth-token',
        result: 'unauthorized-no-header',
        token_status: tokenStatus || 'unknown'
      },
      extra: {
        path,
        method,
        url,
        had_token: !!hadToken,
        ...inspectCognitoStorage(storage)
      }
    }
  )

// 通知の間引き。kind(unavailable / recovered / session-lost)ごとに、直近の通知から cooldownMs 以内なら false を返す
export const createReportThrottle = ({
  cooldownMs = TOKEN_REPORT_COOLDOWN_MS,
  now = () => Date.now()
} = {}) => {
  const lastReportedAt = {}
  return (kind) => {
    const current = now()
    const last = lastReportedAt[kind]
    if (last !== undefined && current - last < cooldownMs) {
      return false
    }
    lastReportedAt[kind] = current
    return true
  }
}

// トークン取得の結果を見て Sentry に通知する(axios の onRequest と middleware の両方から使う)。
// 再試行した結果(retried)だけが対象で、同じ結果オブジェクトからは 1 回しか送らない(同時リクエストや
// middleware → API と続けて同じ結果を見たときの重複防止)。種類ごとの間引きは shouldReport で行う
export const reportTokenResult = ({
  sentry,
  result,
  shouldReport = () => true,
  path,
  method,
  url
}) => {
  if (!result || !result.retried || result.reported) {
    return false
  }
  if (result.status !== 'ok' && result.status !== 'unavailable') {
    return false
  }
  result.reported = true
  const kind = result.status === 'ok' ? 'recovered' : 'unavailable'
  if (!shouldReport(kind)) {
    return false
  }
  const report =
    kind === 'recovered' ? reportTokenRecovered : reportTokenUnavailable
  return report({ sentry, error: result.error, path, method, url })
}
